import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { App, Button, Tabs, Typography } from 'antd';

import Spin from 'components/Spin';
import { FilterActions } from 'v2/components/Pages/Reports/FilterActions';
import { FilterPanel } from 'v2/components/Pages/Reports/FilterPanel';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import TopPage from 'v2/components/Shared/TopPage';
import Tour from 'v2/components/Shared/Tour';
import { useProduct } from 'v2/hooks/products/useProduct';
import { EChargeType, EProductType } from 'v2/interfaces/product';
import { useUserStore } from 'v2/store/User';

import { Icon } from '@eduzz/ui-antd-theme';

import CampaignForm from './components/CampaignForm';
import CampaignTable from './components/CampaignTable';
import SalesPageUrls from './components/CampaignTable/SalesPageUrls';
import CheckoutBooster from './components/CheckoutBooster';
import CheckoutUrlsNew from './components/CheckoutUrlsNew';
import { Offers } from './components/Offers';
import PostbackModal from './components/PostbackModal';
import QuickView from './components/QuickView';
import TrackerForm from './components/TrackerForm';
import UrlCreator from './components/UrlCreator';
import Filter from './Filter';
import { campaignsListTourSteps, campaignsButtonsTourSteps } from './tour';

enum tabType {
  EDUZZ = '0',
  MYLINKS = '1',
  PRODUCER = '2',
  AFFILIATE = '3',
  NONE = '-1'
}

export interface ICheckoutsUrls {
  linkSun?: string;
  linkChk: string;
}

const MyCampaigns: React.FC = () => {
  const { message } = App.useApp();
  const params = useParams<{ productId?: string }>();

  const [currentTab, setCurrentTab] = useState<string>(tabType.NONE);
  const [activeQuickView, setActiveQuickView] = useState(false);
  const [activeUrlCreator, setActiveUrlCreator] = useState(false);
  const [activeCampaignForm, setActiveCampaignForm] = useState(false);
  const [activePostbackForm, setActivePostbackForm] = useState(false);
  const [activeBooster, setActiveBooster] = useState(false);
  const [activeTrackerForm, setActiveTrackerForm] = useState(false);
  const [showTables, setShowTables] = useState(false);
  const [campaignQuick, setCampaignQuick] = useState<any>({});
  const [campaignUrlCreator, setCampaignUrlCreator] = useState('');
  const [campaignUrlCheckoutCreator, setCampaignUrlCheckoutCreator] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<number>(params.productId ? Number(params.productId) : 0);

  const updateListCampaignsRef = useRef<any>(null);

  const {
    product: currentProduct,
    isLoading: loadingProduct,
    error
  } = useProduct(selectedProductId ? Number(selectedProductId) : 0);

  useEffect(() => {
    if (error) {
      message.error('Ocorreu um erro ao tentar carregar o produto');
    }
  }, [error, message]);

  useEffect(() => {
    if (params?.productId) {
      setSelectedProductId(Number(params.productId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentProduct) {
      setShowTables(true);
      setCurrentTab(tabType.EDUZZ);
      return;
    }

    setShowTables(false);
  }, [currentProduct, loadingProduct]);

  const handleChange = useCallback((newTab: string) => {
    setCurrentTab(newTab);
  }, []);

  const handleActiveQuick = useCallback(() => {
    setCampaignQuick({});
    setActiveQuickView(false);
  }, []);

  const handleActiveUrlCreator = useCallback(() => {
    setCampaignUrlCreator('');
    setCampaignUrlCheckoutCreator('');
    setActiveUrlCreator(false);
  }, []);

  const handleChangeSelect = useCallback(async (product: number) => {
    if (!product) {
      setShowTables(false);
      setSelectedProductId(0);
      return;
    }

    setSelectedProductId(product);
  }, []);

  const openTrackerForm = useCallback(() => {
    setActiveTrackerForm(true);
  }, []);

  const closeTrackerForm = useCallback(() => {
    setActiveTrackerForm(false);
  }, []);

  const openUrlCreator = useCallback((campaign: string) => {
    setCampaignUrlCreator(campaign);
    setActiveUrlCreator(true);
  }, []);

  const openUrlCreatorCheckout = useCallback((urls: ICheckoutsUrls) => {
    setCampaignUrlCreator(urls.linkSun ?? '');
    setCampaignUrlCheckoutCreator(urls.linkChk);
    setActiveUrlCreator(true);
  }, []);

  const openQuickView = useCallback((campaign: any) => {
    setCampaignQuick(campaign);
    setActiveQuickView(true);
  }, []);

  const openCampaignForm = useCallback(() => {
    setActiveCampaignForm(true);
  }, []);

  const handleActiveCampaignForm = useCallback(() => {
    setActiveCampaignForm(false);
  }, []);

  const openModalPostback = useCallback(() => {
    setActivePostbackForm(true);
  }, []);

  const closeModalPostback = useCallback(() => {
    setActivePostbackForm(false);
  }, []);

  const closeModalBooster = useCallback(() => {
    setActiveBooster(false);
  }, []);

  const setUpdateListCampaigns = useCallback((updateFunction: any) => {
    updateListCampaignsRef.current = updateFunction;
  }, []);

  const { currentUserId } = useUserStore(({ user }) => ({ currentUserId: user.id }));

  const physicalProductPending = Boolean(
    currentProduct && currentProduct.type === 3 && currentProduct.moderation_status === 'P'
  );
  const isSinglePayment = currentProduct && currentProduct.charge_type === EChargeType.SINGLE;
  const isInfo = currentProduct && currentProduct.type === EProductType.DIGITAL;
  const disabledCreateCampaign =
    currentProduct &&
    !currentProduct.is_producer &&
    (!currentProduct.affiliation_status ||
      currentProduct.affiliation_status === 'R' ||
      currentProduct.affiliation_status === 'P');

  const showProducerProductOffer = currentProduct?.is_producer && isSinglePayment && isInfo;
  const showAffiliateProductOffer = currentProduct?.affiliation_status === 'A' && isSinglePayment && isInfo;
  const showOffers = showProducerProductOffer || showAffiliateProductOffer;

  const tabItems = [
    { key: '0', label: 'Eduzz' },
    ...(currentProduct && !currentProduct.is_producer ? [{ key: '1', label: 'Meus Links' }] : []),
    { key: '2', label: 'Produtor' },
    { key: '3', label: 'Afiliado' }
  ];

  return (
    <div className='animate-fadeIn'>
      <TopPage
        title='Meus Links de Divulgação'
        extra='Links de Checkout, Funis e Campanhas para vender este produto/serviço.'
        disableAlerts
      />

      {!params.productId && !selectedProductId ? (
        <Tour name='campaigns_list_tour' steps={campaignsListTourSteps} />
      ) : null}
      {!params.productId && selectedProductId ? (
        <Tour name='campaigns_buttons_tour' steps={campaignsButtonsTourSteps} />
      ) : null}

      <div id='table__campaigns'>
        <FilterPanel id='campaigns-filter-panel'>
          <div className='grid grid-cols-1 items-end gap-4 md:grid-cols-2'>
            <div id='myProductSelect'>
              <Filter
                onConfirm={handleChangeSelect}
                product={params.productId ? Number(params.productId) : undefined}
              />
              {selectedProductId > 0 &&
                !loadingProduct &&
                currentProduct &&
                currentProduct.moderation_status === 'R' && (
                  <Typography.Text type='danger'>* Este produto está reprovado.</Typography.Text>
                )}
              {selectedProductId > 0 &&
                !loadingProduct &&
                currentProduct &&
                currentProduct.affiliation_status === 'P' && (
                  <Typography.Text type='warning'>
                    * Sua afiliação ainda está pendente, você não será comissionado.
                  </Typography.Text>
                )}
              {selectedProductId > 0 &&
                !loadingProduct &&
                currentProduct &&
                currentProduct.affiliation_status === 'R' && (
                  <Typography.Text type='danger'>
                    * Você não será comissionado, pois sua afiliação está reprovada.
                  </Typography.Text>
                )}
            </div>
            <div>
              <FilterActions>
                <PermissionHide action='myeduzz:links:editar'>
                  <Button id='myUrlCreator' disabled={!showTables} onClick={openModalPostback}>
                    URL Postback
                  </Button>
                </PermissionHide>
                <PermissionHide action='myeduzz:links:criar'>
                  <Button
                    id='myNewCampaign'
                    type='primary'
                    disabled={!showTables || disabledCreateCampaign}
                    onClick={openCampaignForm}
                    icon={<Icon.Plus />}
                  >
                    Criar Campanha
                  </Button>
                </PermissionHide>
              </FilterActions>
            </div>
          </div>
        </FilterPanel>
      </div>

      {loadingProduct && (
        <div className='flex items-center justify-center p-8'>
          <Spin />
        </div>
      )}

      {!loadingProduct && showTables && (
        <div>
          <Tabs activeKey={currentTab} onChange={handleChange} items={tabItems} />

          {currentTab === tabType.EDUZZ && (
            <div>
              <CheckoutUrlsNew
                openTrackerForm={openTrackerForm}
                currentProduct={currentProduct}
                currentUserId={currentUserId}
                physicalProductPending={physicalProductPending}
                openUrlCreatorCheckout={openUrlCreatorCheckout}
              />

              {showOffers && <Offers product={currentProduct} isAffiliate={showAffiliateProductOffer} />}

              <SalesPageUrls />

              {activeTrackerForm && (
                <TrackerForm
                  open={activeTrackerForm}
                  handleClose={closeTrackerForm}
                  product={{ id: currentProduct?.id }}
                  campaign={{ id: -1 }}
                />
              )}
            </div>
          )}

          {!currentProduct?.is_producer && (
            <>
              {currentTab === tabType.MYLINKS && (
                <CampaignTable
                  productId={selectedProductId}
                  type='my-links'
                  openUrlCreator={openUrlCreator}
                  openQuickView={openQuickView}
                  setUpdate={setUpdateListCampaigns}
                  isProducer={false}
                  openUrlCreatorCheckout={openUrlCreatorCheckout}
                />
              )}

              {currentTab === tabType.PRODUCER && (
                <CampaignTable
                  productId={selectedProductId}
                  type='producer'
                  openUrlCreator={openUrlCreator}
                  openUrlCreatorCheckout={openUrlCreatorCheckout}
                  openQuickView={openQuickView}
                  setUpdate={setUpdateListCampaigns}
                  isProducer={false}
                />
              )}

              {currentTab === tabType.AFFILIATE && (
                <CampaignTable
                  productId={selectedProductId}
                  type='affiliate'
                  openUrlCreator={openUrlCreator}
                  openUrlCreatorCheckout={openUrlCreatorCheckout}
                  setUpdate={setUpdateListCampaigns}
                  isProducer={false}
                />
              )}
            </>
          )}

          {currentProduct?.is_producer && (
            <>
              {currentTab === tabType.PRODUCER && (
                <CampaignTable
                  productId={selectedProductId}
                  type='producer'
                  openUrlCreator={openUrlCreator}
                  openUrlCreatorCheckout={openUrlCreatorCheckout}
                  openQuickView={openQuickView}
                  setUpdate={setUpdateListCampaigns}
                  isProducer={true}
                />
              )}

              {currentTab === tabType.AFFILIATE && (
                <CampaignTable
                  productId={selectedProductId}
                  type='affiliate'
                  openUrlCreator={openUrlCreator}
                  openUrlCreatorCheckout={openUrlCreatorCheckout}
                  setUpdate={setUpdateListCampaigns}
                  isProducer={true}
                />
              )}
            </>
          )}
        </div>
      )}

      <QuickView
        active={activeQuickView}
        changeActive={handleActiveQuick}
        product={currentProduct}
        campaign={campaignQuick}
        updateListCampaigns={updateListCampaignsRef.current}
      />

      {activeUrlCreator && (
        <UrlCreator
          open={activeUrlCreator}
          handleClose={handleActiveUrlCreator}
          baseUrl={campaignUrlCreator}
          baseUrlChk={campaignUrlCheckoutCreator}
          isProducer={currentProduct?.is_producer}
        />
      )}

      {activeCampaignForm && (
        <CampaignForm
          open={activeCampaignForm}
          handleClose={handleActiveCampaignForm}
          product={currentProduct}
          producer={currentProduct?.is_producer}
          onChangeTab={handleChange}
          tabActive={currentTab}
          updateListCampaigns={updateListCampaignsRef.current}
        />
      )}

      {activePostbackForm && (
        <PostbackModal open={activePostbackForm} product={currentProduct} onClose={closeModalPostback} />
      )}

      {activeBooster && <CheckoutBooster open={activeBooster} product={currentProduct} onClose={closeModalBooster} />}
    </div>
  );
};

export default MyCampaigns;
